import type { AnalyticsPlacementTypes } from '@core/analytics'
import type { TAuthIntent, TDownloadIntentCollection } from '@features/auth'
import type { TDownloadAudio } from '@features/audio-download'

type TRunAuthIntentDeps = {
    myUsername: string
    follow: (vars: { username: string; myUsername: string }) => void
    like: (audioId: string, placement: AnalyticsPlacementTypes) => void
    resumeDownload: (audio: TDownloadAudio) => void
    downloadCollection: (collection: TDownloadIntentCollection) => void
    // Idempotency guards: skip the mutation when the just-loaded user is already in the target state,
    // so a resume doesn't fire a redundant like/follow (which the backend 422s and whose optimistic
    // rollback would visually undo the real state). `isLiked` loads the list rather than reading the
    // cache: a guest never fetched it, so a cache read here always answered "not liked" and also left
    // the optimistic write with nothing to update. Mirrors audio-web loading liked-ids/follow-check
    // before the post-auth dispatch.
    isLiked: (audioId: string) => Promise<boolean>
    isFollowing: (username: string) => boolean
}

/**
 * Replays a stored auth intent after successful login (pure — the runner wires the feature
 * callbacks). Mirrors audio-web's post-auth dispatch chain (follow / like / download of an audio
 * or a collection), minus the window CustomEvents.
 */
export const runAuthIntent = async (
    intent: TAuthIntent,
    deps: TRunAuthIntentDeps,
): Promise<void> => {
    switch (intent.kind) {
        case 'follow':
            if (!deps.isFollowing(intent.username)) {
                deps.follow({ username: intent.username, myUsername: deps.myUsername })
            }
            break
        case 'like':
            if (!(await deps.isLiked(intent.audioId))) {
                deps.like(intent.audioId, intent.placement)
            }
            break
        case 'download':
            deps.resumeDownload(intent.audio)
            break
        case 'download-collection':
            deps.downloadCollection(intent.collection)
            break
        default:
            break
    }
}
